import { useState } from 'react'
import { FilterBar, type FilterDefinition } from '@/components/FilterBar'
import { FileCard } from '@/components/FileCard'
import { useFiles } from '@/hooks/useFiles'
import { FileText, Loader2 } from 'lucide-react'
import type { FileFilters } from '@/services/file-api'

const FILE_CATEGORY_OPTIONS = [
	{ label: 'All', value: '' },
	{ label: 'Document', value: 'document' },
	{ label: 'Image', value: 'image' },
	{ label: 'Spreadsheet', value: 'spreadsheet' },
	{ label: 'Presentation', value: 'presentation' },
	{ label: 'Audio', value: 'audio' },
	{ label: 'Video', value: 'video' },
	{ label: 'Archive', value: 'archive' },
	{ label: 'Other', value: 'other' },
]

interface FileListProps {
	entityType?: string
	entityId?: string
	currentUserId?: string
	currentUserRole?: string
}

export function FileList({ entityType, entityId, currentUserId, currentUserRole }: FileListProps) {
	const [filterValues, setFilterValues] = useState<Record<string, string>>({})
	const [appliedFilters, setAppliedFilters] = useState<FileFilters>({})

	const filters: FileFilters = {
		...appliedFilters,
		...(entityType ? { entityType } : {}),
		...(entityId ? { entityId } : {}),
	}

	const { data: files, isLoading, error } = useFiles(filters)

	const filterDefinitions: FilterDefinition[] = [
		...(entityType ? [] : [{ key: 'entityType', label: 'Entity Type', type: 'text' as const }]),
		{
			key: 'fileCategory',
			label: 'Category',
			type: 'select' as const,
			options: FILE_CATEGORY_OPTIONS,
		},
		{
			key: 'sharedWithMe',
			label: 'Shared',
			type: 'select' as const,
			options: [
				{ label: 'All files', value: '' },
				{ label: 'Shared with me', value: 'true' },
			],
		},
	]

	const handleApply = () => {
		const applied: FileFilters = {}
		if (filterValues.entityType) applied.entityType = filterValues.entityType
		if (filterValues.fileCategory) applied.fileCategory = filterValues.fileCategory
		if (filterValues.sharedWithMe) applied.sharedWithMe = filterValues.sharedWithMe
		setAppliedFilters(applied)
	}

	const handleClear = () => {
		setFilterValues({})
		setAppliedFilters({})
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Loader2 className="h-6 w-6 animate-spin text-gray-400" />
			</div>
		)
	}

	if (error) {
		return (
			<div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
				Failed to load files: {error instanceof Error ? error.message : 'Unknown error'}
			</div>
		)
	}

	return (
		<div>
			<FilterBar
				filters={filterDefinitions}
				values={filterValues}
				onChange={setFilterValues}
				onApply={handleApply}
				onClear={handleClear}
			/>

			{!files || files.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-12 text-gray-400" data-testid="file-list-empty">
					<FileText className="h-12 w-12 mb-3" />
					<p className="text-sm">No files found</p>
					<p className="text-xs mt-1">Upload a file or adjust your filters</p>
				</div>
			) : (
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="file-list-grid">
					{files.map((file) => (
						<FileCard key={file.fileId} file={file} currentUserId={currentUserId} currentUserRole={currentUserRole} />
					))}
				</div>
			)}
		</div>
	)
}
